import {useLocation, useNavigate, useParams} from "react-router-dom";
import {useUserContext} from "../providers/UserContext";
import {Button} from "../components/common/Button";
import {useCallback, useEffect, useRef, useState} from "react";
import {addChat, addMessage, addThread, editChatTitle, editMessage, getRef, setTyping, viewChat} from "../lib/database";
import { v4 as uuidv4 } from 'uuid';
import {Chat, Message, WorkspaceMember} from "../lib/types";
import {onValue, query, orderByChild, startAt, get, off} from "firebase/database";
import {useInboxContext} from "../providers/InboxProvider";
import {Breadcrumbs} from "../components/common/Breadcrumbs";
import {Icon} from "../components/common/Icon";
import {FormInlineAlert} from "../components/common/FormInlineAlert";
import classNames from "classnames";
import {Message as MessageElement} from "../components/chat/Message";
import {MessageComposer} from "../components/chat/MessageComposer";
import {extractMentions, slateToPlainText} from "../components/chat/PreviewRenderer";
import {clearEditor} from "../components/chat/SlateEditor";
import {useChatListContext} from "../providers/ChatListProvider";
import {MembersModal} from "../components/groups/MembersModal";
import {useModalContext} from "../providers/ModalProvider";
import useTabVisibility from "../lib/hooks/PageVisibility";
import * as repl from "node:repl";
import {truncate} from "../lib/utils/formatting";
import {getItem, removeAll, setItem} from "../lib/localStorage";
import {SystemMessage} from "../components/chat/SystemMessage";
import {useLayoutContext} from "../providers/LayoutProvider";
import {ReactEditor} from "slate-react";
import {Transforms} from "slate";
import {relativeDay} from "../lib/utils/time";
import React from "react";
type sidePanel = "search" | "members" | "threads" | null;

export const ChatPage = ({}: {}) => {
  const navigate = useNavigate();
  const {chatId: id} = useParams();
  const { user, groups, activeWorkspace,setActiveChatId, activity, unreadChats } = useUserContext();
  const {isMobileResolution} = useLayoutContext();
  const { setChatListVisible, chatListVisible, chatList, runRefresh, currentFilter} = useChatListContext();
  const [chat, setChat] = useState<Chat | null>(null);
  const [content, setContent] = useState("");
  const [messages, setMessages] = useState<Message[] >(
    getItem(`messages_${id}`) || []
  );
  const {inbox, isChatInInbox } = useInboxContext();
  const [sidepanel, setSidepanel] = useState<sidePanel>(null);
  const messagesList = useRef<HTMLDivElement>(null);
  const [typing, setTypingUsers] = useState<any>({});
  const [memberCount, setMemberCount] = useState(0);
  const isTabActive = useTabVisibility();
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);
  const [threadingTo, setThreadingTo] = useState<Message | null>(null);
  const [threadSelection, setThreadSelection] = useState<string>("");
  const [replySelection, setReplySelection] = useState<string>("");
  const [threadHtml, setThreadHtml] = useState<string>("");
  const [highlight, setHighlightState] = useState<Message | null>(null);
  const [finishedFirstLoad, setFinishedFirstLoad] = useState(false);
  const [editor, setEditor] = useState<ReactEditor | null>(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [formTitle, setFormTitle] = useState("");
  const [inputWidth, setInputWidth] = useState(0);
  const spanTitleRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (spanTitleRef.current && spanTitleRef.current.offsetWidth) {
      setInputWidth(spanTitleRef.current.offsetWidth + 50);
    }
  }, [chat?.title, editingTitle]);
  const [lastMessageTimestamp, setLastMessageTimestamp] = useState<number>(
    getItem(`lastMessageTimestamp_${id}`) || 0
  );
  const [lastEditTimestamp, setLastEditTimestamp] = useState<number>(
    getItem(`lastEditTimestamp_${id}`) || 0
  );

  const [isAtBottom, setIsAtBottom] = useState(true);
  const [oldMessagesThreshold, setOldMessagesThreshold] = useState<number>(getItem(`oldMessagesThreshold_${id}`) || 0);
  // Cache and Load Messages
  const loadCachedMessages = () => {
    const cachedMessages = getItem(`messages_${id}`) || [];
    setMessages(cachedMessages);
    setLastEditTimestamp(getItem(`lastEditTimestamp_${id}`) || 0);
    setLastMessageTimestamp(getItem(`lastMessageTimestamp_${id}`) || 0);
  };

  const saveMessagesToCache = (messages: Message[]) => {
    if(messages.length > 0) {
      setItem(`messages_${id}`, messages);
    }
  };

  const setThreshhold = () => {
    if (id && unreadChats) {
      setFinishedFirstLoad(false);
      const thisChat = unreadChats.find((chat) => chat.id === id);
      if (thisChat) {
        const messages = Object.values(thisChat.messages).map((message: Message) => message.timestamp);
        const timestamp = Math.min(...messages);
        setOldMessagesThreshold(timestamp - 1);
        setItem(`oldMessagesThreshold_${id}`, timestamp - 1);
      } else {
        setOldMessagesThreshold(Date.now());
        setItem(`oldMessagesThreshold_${id}`, Date.now());
      }
    } else if (!unreadChats) {
      window.setTimeout(() => {
        setThreshhold();
      }, 300)
    }
  }
  useEffect(() => {
    setThreshhold();
    loadCachedMessages();
    setActiveChatId(id || "");
  }, [id]);


  const setHighlight = (message: Message | null) => {
    if(messagesList && messagesList.current && message) {
      setHighlightState(message);
      if (messagesList.current) {
        const el = messagesList.current.querySelector(`[data-message-id="${message.id}"]`);
        if (el) {
          el.scrollIntoView({behavior: "smooth", block: "center"});
        }
      }
    }
  };

  const scrollToBottom = () => {
    if(messagesList && messagesList.current) {
      messagesList.current.scrollTop = messagesList.current.scrollHeight;
    }
  }

  const location = useLocation();
  useEffect(() => {
    if (finishedFirstLoad) {
      const parameters = new URLSearchParams(location.search);
      if (parameters.get("message")) {
        const message = messages.find((m) => m.id === parameters.get("message"));
           if (message) {
          setHighlight(message);
        } else {
          scrollToBottom();
        }
      } else {
        scrollToBottom();
      }
    }
  }, [finishedFirstLoad]);

  useEffect(() => {
    if (finishedFirstLoad && messagesList.current) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage && lastMessage.authorId === user?.uid) {
        scrollToBottom();
      }
      if (isAtBottom) {
        scrollToBottom();
      }
    }
  }, [messages]);
  useEffect(() => {
    setEditingTitle(false);
    if (chat){

      setActiveChatId(id || "");
      if (activeWorkspace) {
        const members = activeWorkspace?.members ? Object.values(activeWorkspace.members) : [];
        const memberCount = members.filter((member: WorkspaceMember) => {
          let listed = false;
          chat.userPermissions && Object.keys(chat.userPermissions).forEach(userId => {
            if (userId === member.id) {
              listed = true;
            }
          });
          chat.groupPermissions && Object.keys(chat.groupPermissions).forEach(groupId => {
            const group = groups.find(group => group.id === groupId);
            if (group) {
              if (group.permissions[member.id] !== undefined || group.visibility === 'public') {
                listed = true;
              }
            }
          });
          return listed;
        }).length;
        setMemberCount(memberCount);
      }
    }
  }, [chat, activeWorkspace, groups]);
  useEffect(() => {
    if (currentFilter === "inbox" && chat && inbox.length > 0 && !isChatInInbox(chat)) {
      navigate("/chats/"+chat.sidebarItemId+"/chat/"+chat.id);
    }
  }, [inbox, currentFilter, inbox, isChatInInbox]);
  useEffect(() => {
    let unsubscribeMessages: any;
    let unsubscribeEdits: any;
    let unsubscribe: any;
    let unsubscribeTyping: any;
    if (id && groups) {
      setActiveChatId(id);
      const chatRef = getRef('chats/'+id)
      unsubscribe = onValue(chatRef, (snapshot) => {
        const data = snapshot.val();
        let permission = false;
        if(!data) {
          setChat(null);
        } else {
          if(user && data.userPermissions && data.userPermissions[user.uid]) {
            permission = true;
          }
          if(data.groupPermissions) {
            const groupPermissions = Object.keys(data.groupPermissions);
            groupPermissions.forEach(groupId => {
              const group = groups.find(group => group.id === groupId);
              if(group && user && (group.visibility === "public" || group.permissions[user.uid] !== undefined)) {
                permission = true;
              }
            });
          }
          if(permission) {
            if (user && isTabActive) {
              viewChat(user, data);
            }
            if(!data.groupPermissions){
              data.groupPermissions = {}
            }
            if(!data.userPermissions){
              data.userPermissions = {}
            }
            setChat(data);
          } else {
            alert("Permission denied");
            navigate("/inbox");
          }
        }
      });
      // Listener for new messages
      const messagesRef = query(
        getRef(`messages/${id}`),
        orderByChild("timestamp"),
        startAt(lastMessageTimestamp > 0 ? lastMessageTimestamp : 0)
      );

      const typingRef = getRef('typing/'+id)
      unsubscribeTyping = onValue(typingRef, (snapshot) => {
        const data = snapshot.val();
        if(!data) {
          setTypingUsers({});
        } else {
          setTypingUsers(data);
        }
      });
      unsubscribeMessages = onValue(messagesRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const newMessages = Object.values(data) as Message[];
          newMessages.sort((a, b) => a.timestamp - b.timestamp);

          const latestTimestamp = Math.max(
            ...newMessages.map((m) => m.timestamp),
            lastMessageTimestamp
          );

          setLastMessageTimestamp(latestTimestamp);
          setItem(`lastMessageTimestamp_${id}`, latestTimestamp);

          // Merge new messages with existing ones
          setMessages((prevMessages) => [
            ...prevMessages.filter(
              (msg) => !newMessages.some((newMsg) => newMsg.id === msg.id)
            ),
            ...newMessages,
          ]);

          saveMessagesToCache([
            ...messages.filter((msg) => !newMessages.some((newMsg) => newMsg.id === msg.id)),
            ...newMessages,
          ]);
          setFinishedFirstLoad(true);
        } else {
          console.warn("No new messages received, keeping current state");
          setFinishedFirstLoad(true);
        }
      });

      // Listener for edited messages
      const editsRef = query(
        getRef(`messages/${id}`),
        orderByChild("editedAt"),
        startAt(lastEditTimestamp > 0 ? lastEditTimestamp : 0)
      );

      unsubscribeEdits = onValue(editsRef, async (snapshot) => {
        const data = snapshot.val();

        if (data) {

          const editedMessages: Message[] = [];
          let messageRef: any;
          for (const [key, value] of Object.entries(data)) {
            messageRef = getRef(`messages/${id}/${key}`)
            off(messageRef);
            const fullMessageSnapshot = await get(messageRef);
            editedMessages.push(fullMessageSnapshot.val());
          }
          const latestEditTimestamp = Math.max(
            ...editedMessages.map((m) => m.editedAt || 0),
            lastEditTimestamp
          );

          setLastEditTimestamp(latestEditTimestamp);
          setItem(`lastEditTimestamp_${id}`, latestEditTimestamp);

          // Update edited messages in the state
          setMessages((prevMessages) =>
            prevMessages.map((msg) =>
              editedMessages.find((edited) => edited.id === msg.id) || msg
            )
          );

          saveMessagesToCache(
            messages.map((msg) =>
              editedMessages.find((edited) => edited.id === msg.id) || msg
            )
          );
        } else {
          console.warn("No edited messages received, keeping current state");
        }
      });
    }

    return () => {
      if (unsubscribeMessages) unsubscribeMessages();
      if (unsubscribeEdits) unsubscribeEdits();
      if (unsubscribe) unsubscribe();
      if (unsubscribeTyping) unsubscribeTyping();
    };
  }, [groups, id, lastMessageTimestamp, lastEditTimestamp, isTabActive]);

  useEffect(() => {
    if(user && chat && isTabActive) {
      viewChat(user, chat);
    }
  }, [chat, isTabActive, messages]);

  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 60000);

    //on escape up remove reply and thread
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setReplyingTo(null);
        setThreadingTo(null);
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      clearInterval(interval);
      window.removeEventListener('keydown', handleKeyDown);
    }
  }, []);
  const typingLabel: WorkspaceMember[] = [];

  for (let typingUser in typing) {
    if(typingUser !== user?.uid && typing[typingUser] >= now - 3000) {
      if (activeWorkspace?.members[typingUser]) {
        typingLabel.push(activeWorkspace?.members[typingUser]);
      }
    }
  }
  useEffect(() => {
    if(sidepanel) {
      setChatListVisible(false);
    } else if (chatList.length > 0) {
      setChatListVisible(true);
    }
  }, [sidepanel]);

  const {openModal}= useModalContext();
  const breadcrubs = (chat?.parents || []).map(chat => ({
    id: chat.id,
    title: chat.title
  }));
  breadcrubs.push({
    id: chat?.id || "",
    title: chat?.title || ""
  })

  const focusEditor = useCallback(() => {
    if (editor) {
      Transforms.select(editor, {offset: 0, path: [0, 0]});
      const editorDiv = document.querySelector(".messageComposer [contenteditable=true]") as HTMLTextAreaElement;
      if(editorDiv) {
        editorDiv.focus();
      }
    }
  }, [editor])

  const [scrollDirection, setScrollDirection] = useState<"up"|"down"|null>("up");
  const [position, setPosition] = useState(20);
  const timer = useRef<NodeJS.Timeout | null>(null);
  const scrollHandler = (e: any ) => {
    if(!e.currentTarget) return;

    const pos = position;
    const newPos = (e.currentTarget as HTMLDivElement).scrollTop;
    if(timer.current) {
      window.clearTimeout(timer.current);
    }
    if(newPos >= pos) {
      setScrollDirection("down");
    } else {
      setScrollDirection("up");
    }
    timer.current = setTimeout(() => {
      setScrollDirection(null);
    }, 3000)
    setPosition(newPos);
  };
  return (
    <div className="flex-grow h-full flex">
      <div className="chatArea flex-grow h-full rounded-[0_8px_8px_0] flex flex-col">
        <div className={
          classNames(
            "h-15 flex-shrink-0 px-4 border-b border-b-gray-6 flex items-center justify-between z-[10]",
            isMobileResolution && "fixed ",
            isMobileResolution
            && ("bg-white w-full fixed transition-all " +
              (scrollDirection === "up" || position < 10
                ? "top-0"
                : "-top-[60px]")
            )
          )
        }>
          <div className="flex gap-2 w-full">
            {isMobileResolution && (
              <Button variant={"tertiary"} icon={"only"} onClick={() => {
                setActiveChatId("");
                if (currentFilter?.startsWith("sidebar_")) {
                  navigate("/chats/"+currentFilter?.split("_")[1]);
                } else {
                  navigate("/inbox");
                }
              }}>
                <Icon name={"arrow_back"}/>
              </Button>
            )}
            <div className="breadcrumbs pr-6 w-full">
              {editingTitle ? (<div
                className={"w-full group font-style-headline-3 inline-flex justify-start gap-2 text-gray-1 font-[300] text-left"}>
                <input
                  style={{ width: inputWidth }}
                  className={"break-all bg-gray-11 pl-2 pr-8 py-1 bg-gray-11 rounded-md"}
                  value={formTitle}
                  onChange={(e) => {
                    setFormTitle(e.target.value);
                    let w = (Math.max(e.target.value.length * 10, inputWidth)+30);
                    if (messagesList.current && w > messagesList.current.offsetWidth - 100) {
                      w = messagesList.current.offsetWidth - 100
                    }
                    e.target.style.width = w + "px";
                  }}
                  onBlur={(e) => {
                    window.setTimeout(() => {
                      // setEditingTitle(false);
                    }, 100);
                  }}
                  autoFocus={true}
                  onKeyUp={(e) => {
                    if (e.key === "Enter") {
                      if (chat && formTitle.trim()) {
                        setChat({...chat, title: formTitle.trim()});
                        editChatTitle(chat, formTitle.trim());
                      }
                      setEditingTitle(false);
                    }
                    if (e.key === "Escape") {
                      setEditingTitle(false);
                    }
                  }}

                />
                <button className={"-ml-10 mt-1 bg-yellow-3 p-1 h-6 w-6 rounded-md flex justify-center items-center pointer-events-none group-hover:pointer-events-auto opacity-100"}
                onClick={()=>{
                  if (chat && formTitle.trim()) {
                    setChat({...chat, title: formTitle.trim()});
                    editChatTitle(chat, formTitle.trim());
                  }
                  setEditingTitle(false);
                }}>
                  <Icon name={"check"} size={"xsmall"} />
                </button>
              </div>) : (<div
                  className={classNames(
                    "rounded-md group font-style-headline-3 inline-flex justify-between gap-2 text-gray-1 font-[300] text-left px-2 py-1",
                    chat?.authorId === user?.uid && "hover:bg-gray-11"
                  )}>
                  <span className={"line-clamp-1 break-all"} ref={spanTitleRef}>{chat?.title}</span>
                {chat?.authorId === user?.uid && (<button className={classNames(
                    " bg-yellow-3 p-1 h-6 w-6 rounded-md flex justify-center items-center pointer-events-none group-hover:pointer-events-auto opacity-0 group-hover:opacity-100",
                  )}
                          onClick={()=>{
                            if (chat && chat.authorId === user?.uid) {
                              setFormTitle(chat.title);
                              setEditingTitle(true);
                            }
                          }}>
                    <Icon name={"edit"} size={"xsmall"} />
                  </button>)}
                </div>)}

            </div>
          </div>
          <div className="actions flex gap-1">
            <Button variant={"tertiary"} icon={"only"} className="relative hidden"><Icon name={"more_vert"}/></Button>
            <Button variant={"tertiary"} icon={"only"} className="relative hidden" onClick={() => {
              setSidepanel(sidepanel === "search" ? null : "search");
            }}><Icon name={"search"}/></Button>
            <Button variant={"tertiary"} icon={"only"} className={"relative"} onClick={() => {
              setSidepanel(sidepanel === "members" ? null : "members");
            }}><Icon name={"group_add"}/>
              <div
                className="counter absolute top-[-6px] right-[-6px] w-4 h-4 flex items-center justify-center rounded-full font-style-label-xs font-bold">
                {memberCount}
              </div>
            </Button>
            <Button variant={"tertiary"} icon={"only"} className="relative hidden" onClick={() => {
              setSidepanel(sidepanel === "threads" ? null : "threads");
            }}
            >
              <Icon name={"chat"}/>
              <div
                className="counter absolute top-[-6px] right-[-6px] bg-gray-7 w-4 h-4 flex items-center justify-center rounded-full font-style-body-xs">1
              </div>
            </Button>
          </div>
        </div>
        <div className={
          classNames(
            "chat flex-shrink flex-grow overflow-auto overflow-x-hidden p-4 pt-6 flex flex-col gap-1",
            isMobileResolution && "pt-[78px]"
          )}
          ref={messagesList} onScroll={(e) => {
          scrollHandler(e);
          const el = e.target as HTMLDivElement;
          if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
            setIsAtBottom(true);
          } else {
            setIsAtBottom(false);
          }
        }}>

          {chat?.threadSource && (
            <MessageElement
              key={chat.threadSource.id}
              author={chat.threadSource.authorName}
              content={chat.threadSource.content}
              timestamp={chat.threadSource.timestamp}
              isOriginal={true}
              isRead={true}
              message={chat.threadSource}
              lastMessage={false}
              scrollChatToBottom={scrollToBottom}
              scrollToMessage={(message) => {
              }}
              highlight={false}
              threadText={chat.threadHtml || ""}
              chat={chat}
              chatEditor={editor || undefined}
              />
          )}
          {!!chat && messages.map((message: Message, i: number) => {
            const previousMessage = i > 0 ? messages[i - 1] : null;
            const isRead = message.timestamp <= oldMessagesThreshold;
            const isPreviousRead = i > 0 && messages[i - 1].timestamp <= oldMessagesThreshold;
            let divider = "";
            let dividerTitle = "";
            const previousDate = i > 0 ? new Date(messages[i - 1].timestamp).toDateString() : "";
            const currentDate = new Date(message.timestamp).toDateString();
            if (i > 0 && previousDate !== currentDate) {
              divider = relativeDay(message.timestamp)
              dividerTitle = currentDate;
            }
            if (!isRead && isPreviousRead) {
              divider = (messages.length - i) + " new";
              dividerTitle = (messages.length - i) + " new since "+previousDate;
            }
            const showDivider = !!divider;
            const showAuthor =
              !previousMessage ||
              previousMessage.authorId !== message.authorId ||
              message.parent ||
              showDivider ||
              message.timestamp - previousMessage.timestamp > 60 * 1000 * 5;

            const isLastAuthor = messages.slice(i + 1).findIndex(m => m.authorId !== message.authorId) === -1;
            const showTimestamp = showAuthor && isLastAuthor;

            return (
              <React.Fragment key={message.id}>
                {showDivider && (
                  <div className="flex justify-start pl-16 items-center mt-4 mb-2" key={message.id + "_divider"}>
                     <div className={classNames(
                       "font-style-label-sm rounded-full",
                       isRead ? "text-gray-3" : "text-gray-1"
                     )}
                          title={dividerTitle}>
                       {divider}
                    </div>

                  </div>
                )}
                { message.system ? (
                    <SystemMessage
                      key={message.id} author={message.authorName} content={message.content}
                      timestamp={message.timestamp}
                      isOriginal={false}
                      isRead={message.timestamp <= oldMessagesThreshold}
                      message={message}
                      showAuthor={!!showAuthor}
                      showTimestamp={!!showTimestamp}
                      previousMessage={previousMessage || undefined}
                      lastMessage={i === messages.length - 1}
                      scrollChatToBottom={scrollToBottom}
                      scrollToMessage={(message) => {
                        setHighlight(message)
                      }}
                      highlight={!!(highlight && highlight.id === message.id)}
                      />
                ) :
                  (<MessageElement key={message.id} author={message.authorName} content={message.content}
                                timestamp={message.timestamp}
                                isOriginal={false}
                                isRead={message.timestamp <= oldMessagesThreshold}
                                message={message}
                                showAuthor={!!showAuthor || message.deleted === true || previousMessage?.deleted === true}
                                showTimestamp={!!showTimestamp}
                                previousMessage={previousMessage || undefined}
                                lastMessage={i === messages.length - 1}
                                scrollChatToBottom={scrollToBottom}
                                onReply={(message, text="") => {
                                  setThreadingTo(null);
                                  setReplyingTo(message);
                                  setReplySelection(text);
                                  focusEditor();
                                }}
                                onThread={(message, markedHtml, selectedText) => {
                                  setReplyingTo(null);
                                  setThreadingTo(message);
                                  setThreadSelection(selectedText);
                                  setThreadHtml(markedHtml);
                                  focusEditor();
                                }}
                                scrollToMessage={(message) => {
                                  setHighlight(message)
                                }}
                                highlight={!!(highlight && highlight.id === message.id)}
                                chat={chat}
                                chatEditor={editor || undefined}
                />)}
              </React.Fragment>
            )}
          )}
        </div>
        <div className="lg:p-6 mt-auto">
          {typingLabel.length > 0 && (
            <div className="flex gap-2 items-center my-1">
              <Icon name={"chat"} size={"2xsmall"}/>
              <span
                className="font-style-body-xxs text-gray-3"> {typingLabel.map(user => user.displayName).join(", ")} is typing...</span>
            </div>
          )}
          {replyingTo && (
            <div className={"bg-gray-8 relative rounded-t-lg"}>
              <div className="flex items-center gap-2 w-full">
                <div className="w-6 flex-shrink-0">
                  <span className="absolute top-[1px] left-2 mt-0 origin-center rotate-180" style={{
                    transform: "scaleX(-1)"
                  }}>
                    <Icon name={"reply"} size={"xsmall"}/>
                  </span>
                </div>

                <div className="flex gap-1">
                  <div className="font-style-body-xs text-gray-1 flex-shrink-0">{replyingTo.authorName}: </div>
                  <div className="font-style-body-xs text-gray-3 line-clamp-1">{replySelection || slateToPlainText(JSON.parse(replyingTo.content))}</div>
                </div>
                <Button className={"ml-auto"} variant={"tertiary"} icon={"only"} onClick={() => setReplyingTo(null)}><Icon name={"close"} size={"2xsmall"}/></Button>
              </div>
            </div>
          )}
          {threadingTo && (
            <div className={"bg-gray-8 relative rounded-t-lg"}>
              <div className="flex items-center gap-2 w-full">
                <div className="w-6 flex-shrink-0">
                  <span className="absolute top-[1px] left-2 mt-0 origin-center rotate-180" style={{
                    transform: "scaleX(-1)"
                  }}>
                    <Icon name={"chat_add_on"} size={"xsmall"}/>
                  </span>
                </div>

                <div className="flex gap-1">
                  <div className="font-style-body-xs text-gray-1 flex-shrink-0">{threadingTo.authorName}: </div>
                  <div className="font-style-body-xs text-gray-3 line-clamp-1">{truncate(threadSelection, 60)}</div>
                </div>
                <Button className={"ml-auto"} variant={"tertiary"} icon={"only"} onClick={() => setThreadingTo(null)}><Icon name={"close"} size={"2xsmall"}/></Button>
              </div>
            </div>
          )}
          <div className="messageComposer">
            <MessageComposer
              onReady={(editor) => {
                setEditor(editor);
              }}
              onSend={async (e) => {
              const content = slateToPlainText(e.children).trim();
              if (user && activeWorkspace && id && chat && content) {
                if(threadingTo) {
                  const chatId = uuidv4();
                  const groupPermissions = chat.groupPermissions;
                  const userPermissions: Record<string, 'write'> = {
                    [user.uid]: 'write',
                    [threadingTo.authorId]: 'write',
                  };
                  const newMessage: Message = {
                    id: uuidv4(),
                    chatId: chatId,
                    content: JSON.stringify(e.children),
                    plainText: slateToPlainText(e.children),
                    timestamp: Date.now(),
                    visibility: 'public',
                    authorId: user.uid,
                    authorName: user.displayName || "",
                    mentions: extractMentions(e.children),
                  }
                  const threadChat: Chat = {
                    id: chatId,
                    groupId: chat.groupId,
                    workspaceId: activeWorkspace.id,
                    title: threadSelection,
                    summary: slateToPlainText(e.children),
                    timestamp: Date.now(),
                    authorId: user.uid,
                    authorName: user.displayName || "",
                    parents: [
                      ...(chat.parents || []),
                      chat
                    ],
                    lastActiveAt: Date.now(),
                    lastMessage: newMessage,
                    userPermissions,
                    groupPermissions,
                    threadSource: threadingTo,
                    threadContent: threadSelection,
                    threadHtml: threadHtml,
                    createdAt: Date.now(),
                  };
                  await addChat(threadChat)
                  await addMessage(newMessage, user, threadChat);
                  await addThread(threadingTo, threadChat);
                  setThreadingTo(null);
                  setThreadSelection("");
                  setThreadHtml("");
                  navigate(`/chat/${threadChat.id}`);
                } else {
                  await addMessage({
                    id: uuidv4(),
                    chatId: id,
                    content: JSON.stringify(e.children),
                    plainText: slateToPlainText(e.children),
                    timestamp: Date.now(),
                    visibility: 'public',
                    authorId: user?.uid || "",
                    authorName: user?.displayName || "",
                    mentions: extractMentions(e.children),
                    parent: replyingTo ? replyingTo : null,
                    replySelection: replySelection,
                  }, user, chat);
                  setReplyingTo(null);
                  clearEditor(e);
                  setTyping(user, chat, false);
                }
                runRefresh();
                scrollToBottom();
              }
            }}
                             onType={() => {
                               if (user && chat) {
                                 setTyping(user, chat);
                               }
                             }}/>
          </div>
        </div>
      </div>
      <div className={classNames(
          "w-0 transition-all overflow-hidden bg-white border-l border-l-gray-6",
          isMobileResolution ? "fixed top-0 right-0 bottom-0 z-50" : "flex-shrink-0",
          sidepanel && !isMobileResolution ? "w-[300px]" : "w-0",
          sidepanel && isMobileResolution ? "w-full" : "w-0"
        )
      }>
        {sidepanel === "members" && (
          <div className="flex flex-col w-full">
            <div className="flex items-center justify-between px-4 mt-4 mb-2">
              <div className="breadcrumbs">People in this chat</div>
              <Button variant={"tertiary"} icon={"only"} onClick={() => {
                setSidepanel(null);
              }}><Icon name={"close"}/></Button>
            </div>
            <div className={"flex flex-col gap-4"}>
              {chat?.userPermissions && Object.keys(chat.userPermissions).map(userId => {
                const member = activeWorkspace?.members[userId];
                const permission = chat.userPermissions && chat.userPermissions[userId];
                if (member) {
                  return (
                    <div key={userId} className="flex items-center gap-2 px-4">
                      <div className="avatar rounded-full bg-gray-3">
                        <img src={member.avatar} alt={member.displayName} className={"w-6 h-6 rounded-full"}/>
                      </div>
                      <div className="flex flex-col font-style-body-md">
                        <div>
                          {member.displayName}
                          {permission === "owner" && (<span className={"bg-gray-6 p-1 ml-2 font-style-label-xs rounded-sm"}>OP</span>)}
                          {permission === "admin" && (<span className={"bg-gray-6 p-1 ml-2 font-style-label-xs rounded-sm"}>Admin</span>)}
                          {permission === "read" && (<span className={"p-1 ml-2 font-style-label-xs rounded-sm"}>read only</span>)}
                        </div>
                      </div>
                    </div>
                  )
                }
              })}
            </div>
            {chat?.groupPermissions && Object.keys(chat.groupPermissions).length > 0 && (
              <div className="flex items-center justify-between px-4 mt-6 mb-2">
                <div>Groups in this chat</div>
              </div>
            )}
            <div className={"flex flex-col gap-2"}>
              {chat?.groupPermissions && Object.keys(chat.groupPermissions).map((groupId: string) => {
                const group = groups.find(group => group.id === groupId);
                if (group) {
                  return (
                    <button key={group.id} className="flex items-center gap-2 px-4 text-left" onClick={() => {
                      openModal(<MembersModal group={group} />)
                    }}>
                      <div className="avatar rounded-full">
                        {group.emoji}
                      </div>
                      <div className="flex flex-col font-style-body-md">
                        <div>
                          {group.name}
                        </div>
                        <div className="font-stylex-body-xxs text-gray-3">
                          {group.visibility === "public" ? "everyone" : (Object.keys(group.permissions).length + " members")}

                        </div>
                      </div>
                    </button>
                  )
                }
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}